/**
 * ListContextProvider - контекст провайдер для ListStore
 */
import type { IListConfig } from '@/Core/Contracts/UI/List'
import { createContext, useContext } from 'react'
import { configManager } from '../../Managers/Config'
import { EventManager } from '../../Managers/Event/EventManager'
import { MergeConfigAndPropsTask } from '../../Tasks/Utils/MergeConfigAndPropsTask'
import { ListStore } from './Stores/ListStore'
import { LoadActionTask } from './Tasks/LoadActionTask'
import { LoadNextPageTask } from './Tasks/LoadNextPageTask'
import { LoadPrevPageTask } from './Tasks/LoadPrevPageTask'
import { DefaultTemplate } from './Templates/DefaultTemplate'

const ListContext = createContext<ListStore | undefined>(undefined)

/**
 * Створює новий ListStore з заданою конфігурацією
 *
 * @param config - конфігурація списку (IListConfig)
 * @returns екземпляр ListStore або null якщо конфігурація некоректна
 */
export const createListContext = (config: IListConfig = {}): ListStore | null => {
    if (!config.restUrl) {
        console.warn('List component: "restUrl" is required.')
        return null
    }

    // Отримуємо конфігурацію List
    const listConfig = configManager.get('ListConfig', {})
    // Об'єднуємо конфігурацію з пропсами через MergeConfigAndPropsTask
    const mergedProps = MergeConfigAndPropsTask({
        listConfig,
        listProps: config,
    }) || config

    // Ініціалізація states
    mergedProps.states ??= {}
    mergedProps.states['total'] = 0

    // Шаблон за замовчуванням
    mergedProps.template ??= DefaultTemplate

    // Налаштування профілювання для dev режиму
    const env = (import.meta as any).env
    if (!mergedProps.debug && env?.DEV) {
        mergedProps.debug = {
            id: `list-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
            name: 'List',
            type: 'component',
            path: '/Core/UI/List',
            enabled: true,
        }
    }

    // Створюємо тимчасовий eventManager для виконання onBeforeInit
    const tempEventManager = new EventManager()
    
    // Виконуємо всі onBeforeInit події з переданих пропсів
    if (mergedProps.eventHandlers?.['onBeforeInit']) {
        Object.entries(mergedProps.eventHandlers['onBeforeInit']).forEach(([name, handler]) => {
            tempEventManager.on('onBeforeInit', name, handler as any)
        })
    }
    
    // Emit onBeforeInit події - передаємо props по ссылці для можливості змін
    tempEventManager.emit('onBeforeInit', { props: mergedProps })

    const listContext = new ListStore(mergedProps)

    // Встановлюємо дії списку
    listContext.setActions({
        load: (forceRefresh = false) => LoadActionTask({ listContext, forceRefresh }),
        loadNextPage: () => LoadNextPageTask({ listContext }),
        loadPrevPage: () => LoadPrevPageTask({ listContext }),
    })

    // Виконуємо onAfterInit події з eventManager створеного екземпляра
    listContext.getEventManager().emit('onAfterInit', { listContext })

    return listContext
}

/**
 * Хук для отримання ListContext
 */
export const useListContext = (): ListStore | null => {
    const context = useContext(ListContext)
    return context ?? null
}

/**
 * Provider компонент для ListContext
 */
export const ListContextProvider = ListContext.Provider
